import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    LayoutDashboard, BookOpen, ClipboardCheck, Thermometer, Users,
    AlertTriangle, BarChart3, LogOut, Menu, X, Truck, GraduationCap,
    CalendarCheck, UtensilsCrossed, Building2, Settings, Tag, Eye, XCircle,
    Gauge, Package, ShieldAlert, Barcode
} from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { adminApi } from '../lib/api';

const LOGO_URL = "/logo.svg";

const getNavItems = (role, hasBusiness) => {
    const items = [];

    if (role === 'admin') {
        items.push(
            { path: '/admin', icon: LayoutDashboard, label: 'Platform Overview' },
            { path: '/admin/businesses', icon: Building2, label: 'Businesses' },
            { path: '/admin/coupons', icon: Tag, label: 'Coupons' },
            { path: '/admin/users', icon: ShieldAlert, label: 'Administrators' },
        );
        if (hasBusiness) {
            items.push({ divider: true, label: 'Business Context' });
        }
    }

    if (hasBusiness || role !== 'admin') {
        items.push(
            { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
            { path: '/safe-methods', icon: BookOpen, label: 'Safe Methods', featureKey: 'safe_methods' },
            { path: '/diary', icon: ClipboardCheck, label: 'Daily Diary', featureKey: 'daily_diary' },
            { path: '/temperature', icon: Thermometer, label: 'Temperature', featureKey: 'temperature' },
            { path: '/saved-items', icon: Barcode, label: 'Appliances', featureKey: 'temperature' },
            { path: '/allergens', icon: UtensilsCrossed, label: 'Allergens', featureKey: 'allergens' },
            { path: '/suppliers', icon: Truck, label: 'Suppliers', featureKey: 'suppliers' },
            { path: '/training', icon: GraduationCap, label: 'Training', featureKey: 'training' },
            { path: '/incidents', icon: AlertTriangle, label: 'Incidents', featureKey: 'incidents' },
            { path: '/calibration', icon: Gauge, label: 'Calibration', featureKey: 'calibration' },
            { path: '/deliveries', icon: Package, label: 'Deliveries', featureKey: 'deliveries' },
            { path: '/labels', icon: Tag, label: 'Labels', featureKey: 'labels' },
        );
        if (role !== 'staff') {
            items.push(
                { path: '/reviews', icon: CalendarCheck, label: '4-Weekly Review', featureKey: 'reviews' },
                { path: '/reports', icon: BarChart3, label: 'Reports', featureKey: 'reports' },
                { path: '/users', icon: Users, label: 'Staff Management' },
                { path: '/settings', icon: Settings, label: 'Settings' },
            );
        }
    }
    return items;
};

const Layout = ({ children }) => {
    const { user, business, viewingBusinessId, logout, isAdmin, isFeatureEnabled, updateToken } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [buildVersion, setBuildVersion] = useState(null);

    useEffect(() => {
        fetch('/build-info.json?t=' + Date.now()).then(r => r.json())
            .then(d => setBuildVersion(d)).catch(() => {});
    }, []);

    // Convert hex color to HSL string for Shadcn CSS variables
    const hexToHSL = (hex) => {
        const r = parseInt(hex.slice(1, 3), 16) / 255;
        const g = parseInt(hex.slice(3, 5), 16) / 255;
        const b = parseInt(hex.slice(5, 7), 16) / 255;
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        const l = (max + min) / 2;
        if (max === min) return `0 0% ${Math.round(l * 100)}%`;
        const d = max - min;
        const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        let h = 0;
        if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
        else if (max === g) h = ((b - r) / d + 2) * 60;
        else h = ((r - g) / d + 4) * 60;
        return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    };

    // Determine if a hex color is "light" (needs dark text) or "dark" (needs white text)
    const contrastFg = (hex) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        // WCAG relative luminance
        const L = 0.2126 * (r / 255) + 0.7152 * (g / 255) + 0.0722 * (b / 255);
        return L > 0.45 ? '#0f172a' : '#ffffff';
    };

    // Apply business branding as CSS custom properties
    useEffect(() => {
        const root = document.documentElement;
        const b = business?.branding;
        const primary = b?.primary_color || '#f2c24b';
        const secondary = b?.secondary_color || '#0B1120';
        const accent = b?.accent_color || '#1E293B';
        root.style.setProperty('--brand-primary', primary);
        root.style.setProperty('--brand-primary-fg', contrastFg(primary));
        root.style.setProperty('--brand-secondary', secondary);
        root.style.setProperty('--brand-accent', accent);
        // Sync Shadcn --primary so bg-primary matches brand
        root.style.setProperty('--primary', hexToHSL(primary));
        root.style.setProperty('--primary-foreground', hexToHSL(contrastFg(primary)));
    }, [business]);

    const hasBusiness = !!business || !!viewingBusinessId;
    const allNavItems = getNavItems(user?.role, hasBusiness);
    const navItems = allNavItems.filter(item =>
        item.divider || !item.featureKey || isFeatureEnabled(item.featureKey)
    );

    const handleLogout = () => { logout(); navigate('/login'); };

    const handleClearContext = async () => {
        try {
            const { data } = await adminApi.clearContext();
            updateToken(data.access_token);
            navigate('/admin');
        } catch {}
    };

    const mobileNavItems = navItems.filter(i => !i.divider).slice(0, 5);

    return (
        <div className="min-h-screen bg-slate-950">
            {/* Admin viewing banner */}
            {isAdmin && viewingBusinessId && (
                <div className="fixed top-0 left-0 right-0 z-[60] bg-amber-500/90 backdrop-blur-sm text-slate-950 text-center py-1.5 px-4 text-sm font-medium flex items-center justify-center gap-3" data-testid="admin-context-banner">
                    <Eye className="h-4 w-4" />
                    <span>Viewing: {business?.name || 'Business'}</span>
                    <Button size="sm" variant="outline" className="h-6 text-xs border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-amber-400" onClick={handleClearContext}>
                        <XCircle className="h-3 w-3 mr-1" /> Exit
                    </Button>
                </div>
            )}

            {/* Desktop Sidebar */}
            <aside className={cn("sidebar hidden md:flex flex-col", isAdmin && viewingBusinessId && "pt-9")}>
                <div className="p-5 border-b border-slate-800">
                    <Link to={isAdmin ? '/admin' : '/dashboard'} className="flex items-center gap-3">
                        <img src={business?.branding?.logo_url || LOGO_URL} alt="Food Safe Hub" className="h-10 w-auto max-w-[160px] object-contain" />
                    </Link>
                    {business && (
                        <p className="text-xs mt-2 truncate font-medium" style={{ color: 'var(--brand-primary)', opacity: 0.8 }}>{business.name}</p>
                    )}
                    {!business && isAdmin && (
                        <p className="text-xs text-slate-500 mt-2">Platform Administration</p>
                    )}
                </div>

                <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
                    {navItems.map((item, i) =>
                        item.divider ? (
                            <div key={i} className="pt-3 pb-1 px-3">
                                <span className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">{item.label}</span>
                            </div>
                        ) : (
                            <Link key={item.path} to={item.path} data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
                                className={cn('sidebar-nav-item', location.pathname === item.path && 'active')}>
                                <item.icon className="h-4 w-4" />
                                <span className="text-sm">{item.label}</span>
                            </Link>
                        )
                    )}
                </nav>

                <div className="p-4 border-t border-slate-800">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="h-9 w-9 rounded-full brand-bg-subtle flex items-center justify-center shrink-0">
                            <span className="brand-text font-semibold text-sm">{user?.name?.charAt(0).toUpperCase()}</span>
                        </div>
                        <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-200 truncate">{user?.name}</p>
                            <p className="text-[11px] text-slate-500 capitalize">{user?.role}</p>
                        </div>
                    </div>
                    <Button variant="ghost" className="w-full justify-start text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 h-8 text-xs"
                        onClick={handleLogout} data-testid="logout-button">
                        <LogOut className="h-3.5 w-3.5 mr-2" /> Sign Out
                    </Button>
                    {buildVersion && (
                        <p className="text-[10px] text-slate-600 mt-2 text-center select-all" data-testid="build-version" title={buildVersion.display}>
                            v{buildVersion.version}
                        </p>
                    )}
                </div>
            </aside>

            {/* Mobile Header */}
            <header className={cn("app-header md:hidden flex items-center justify-between", isAdmin && viewingBusinessId && "mt-9")}>
                <Link to={isAdmin ? '/admin' : '/dashboard'} className="flex items-center gap-2">
                    <img src={business?.branding?.logo_url || LOGO_URL} alt="Food Safe Hub" className="h-9 w-auto max-w-[140px] object-contain" />
                </Link>
                <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
                    {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </Button>
            </header>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-50 md:hidden">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
                    <div className="absolute top-0 right-0 w-72 h-full bg-slate-950 border-l border-slate-800 p-4 overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <span className="text-lg font-semibold text-slate-200">Menu</span>
                            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)}><X className="h-5 w-5" /></Button>
                        </div>
                        <nav className="space-y-0.5">
                            {navItems.filter(i => !i.divider).map((item) => (
                                <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}
                                    className={cn('sidebar-nav-item', location.pathname === item.path && 'active')}>
                                    <item.icon className="h-5 w-5" /><span>{item.label}</span>
                                </Link>
                            ))}
                        </nav>
                        <div className="mt-6 pt-4 border-t border-slate-800">
                            <Button variant="ghost" className="w-full justify-start text-slate-400 hover:text-rose-400" onClick={handleLogout}>
                                <LogOut className="h-4 w-4 mr-2" /> Sign Out
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content */}
            <main className={cn("main-content", isAdmin && viewingBusinessId && "pt-9")}>
                <div className="p-4 md:p-6 lg:p-8">{children}</div>
            </main>

            {/* Mobile Bottom Nav */}
            <nav className="mobile-nav pb-safe">
                <div className="flex items-center justify-around py-2">
                    {mobileNavItems.map((item) => (
                        <Link key={item.path} to={item.path}
                            className={cn('mobile-nav-item', location.pathname === item.path && 'active')}>
                            <item.icon className="h-5 w-5" />
                            <span className="text-xs mt-1">{item.label.split(' ')[0]}</span>
                        </Link>
                    ))}
                </div>
            </nav>
        </div>
    );
};

export default Layout;
